import React, { useState } from 'react';
import {
  FiDatabase,
  FiLayers,
  FiSettings,
  FiLock,
  FiArrowRight,
  FiCheck,
  FiBarChart,
  FiCpu,
  FiTrendingUp
} from 'react-icons/fi';
import './Platform.css';

const Platform = () => {
  const [activePlatform, setActivePlatform] = useState('ai');

  const metrics = [
    { value: '3', label: '自研平台', desc: 'AI智能审核 · BIM+造价一体化 · 全过程造价管控' },
    { value: '98.6%', label: 'AI审核准确率', desc: '机器学习模型持续迭代，覆盖十万级历史数据' },
    { value: '60%', label: '效率提升', desc: 'BIM模型直接出量 + 设计变更联动' },
    { value: '100%', label: '过程留痕率', desc: '招投标、审核、审批流程全程电子化存证' }
  ];

  const platforms = {
    ai: {
      title: 'AI智能审核平台',
      icon: <FiCpu />,
      subtitle: '深度学习模型驱动的造价数据智能审核引擎',
      description:
        '基于机器学习、NLP、知识图谱等技术，实现图纸识别、工程量算量、定额匹配、异常识别、历史对标等智能功能，形成“AI预审 + 专家复核”的高效审核体系。',
      modules: [
        '图纸识别：自动解析 CAD/BIM 图纸，提取构件、分部分项工程量',
        '智能算量：土建、安装、市政、装饰全专业自动算量，支持清单导出',
        '定额智能匹配：基于 NLP 与知识图谱自动匹配最优定额子目',
        '异常检测：自动识别多算漏算、价格异常、合同条款冲突等问题',
        '对标分析：对接 50 万+ 历史项目数据库，提供价格对标与指标分析',
        '人机协同：AI 初审生成问题清单，造价专家在线复核确认'
      ],
      gtm: [
        '适用于政府审计、财政评审、央国企投资复核、社会资本项目审计',
        '支持本地部署、私有云部署，满足数据安全及合规要求',
        '可与已有造价系统、项目管理平台通过 API 打通，实现数据联动'
      ],
      cta: {
        title: '想了解 AI 如何助力造价审核？',
        actions: [
          { label: '预约平台演示', link: '/contact', primary: true },
          { label: '下载产品白皮书', link: '/platform#whitepaper' }
        ]
      }
    },
    bim: {
      title: 'BIM+造价一体化平台',
      icon: <FiLayers />,
      subtitle: '从三维模型到造价数据的无缝连接，实现正向设计与成本协同',
      description:
        '支持 Revit、PKPM、鸿业等主流 BIM 模型导入，提供三维可视化算量、设计变更联动、5D 模型管理等功能，让设计、造价、施工多专业协同。',
      modules: [
        '模型导入：兼容主流 BIM 软件，支持构件属性清洗与标准化',
        '三维可视化算量：选择构件即可生成工程量清单、费用分布图',
        '设计变更联动：模型更新实时同步工程量与造价数据',
        '多专业协同：土建、钢筋、机电、幕墙等专业统一工作底座',
        '碰撞检测：提前识别设计冲突，减少变更签证',
        '5D 管理：进度、成本、资源、质量、安全五维联动管理'
      ],
      gtm: [
        '适用于大型建筑、医院、交通枢纽、工业园区等多项目协同场景',
        '与施工现场手机端联动，实现“图模合一”和现场数据回传',
        '可与企业造价指标库对接，沉淀标准化造价数据资产'
      ],
      cta: {
        title: '希望构建 BIM+造价协同体系？',
        actions: [
          { label: '获取方案咨询', link: '/contact', primary: true },
          { label: '查看成功案例', link: '/projects#cost' }
        ]
      }
    },
    control: {
      title: '全过程造价管控平台',
      icon: <FiSettings />,
      subtitle: '投资决策、设计、招标、施工、结算全周期造价数据中枢',
      description:
        '围绕“目标成本 → 合同成本 → 实际成本”构建动态成本控制体系，提供投资测算、过程成本监控、风险预警、报表分析等功能，实现造价治理可视化、可穿透。',
      modules: [
        '投资测算：可研、估算、概算、预算四算对比与自动校核',
        '合同管理：合同价、变更签证、进度款、索赔等信息统一管理',
        '动态成本曲线：目标成本 vs 预算 vs 实际成本实时对比分析',
        '资金计划：按形象进度编制资金计划、现金流预测模型',
        '风险预警：超概算、超合同、进度偏差等多维度预警提示',
        '绩效与审计：形成审计底稿与绩效评价数据，支持多部门共享'
      ],
      gtm: [
        '适用于政府投资项目、央国企集采及多项目集团化管理',
        '支持与 ERP、财务、工程管理系统集成，形成统一数据面',
        '可对接 AI 审核和 BIM 平台，构建“数据中枢+应用层”生态'
      ],
      cta: {
        title: '计划打造造价数据中枢？',
        actions: [
          { label: '预约数字化评估', link: '/contact', primary: true },
          { label: '下载实施方法论', link: '/platform#methodology' }
        ]
      }
    }
  };

  const current = platforms[activePlatform];

  return (
    <main className="platform-page">
      <section className="platform-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-copy">
              <span className="hero-eyebrow">SIDAO DIGITAL SUITE</span>
              <h1>AI + BIM + 全过程管控 · 构建造价治理数字底座</h1>
              <p>
                自研三大平台覆盖造价审核、设计协同、成本管控全生命周期，为政府、央国企、投资机构提供安全可控、快速落地的数字化解决方案。
                平台支持本地化部署、私有云部署，符合政企数据安全与合规要求。
              </p>
            </div>
            <div className="hero-metrics" >
              {metrics.map((metric, index) => (
                <div key={index} className="metric-card">
                  <span className="metric-value">{metric.value}</span>
                  <span className="metric-label">{metric.label}</span>
                  <p>{metric.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="platform-tabs">
        <div className="container">
          <div className="tab-list" style={{ flexDirection: 'row' }}>
            {Object.keys(platforms).map((key) => (
              <button
                key={key}
                className={`tab-item ${activePlatform === key ? 'active' : ''}`}
                onClick={() => setActivePlatform(key)}
              >
                <span className="tab-icon">{platforms[key].icon}</span>
                <span>{platforms[key].title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="platform-detail">
        <div className="container">
          <div className="platform-header">
            <div className="platform-icon-large">{current.icon}</div>
            <div>
              <h2>{current.title}</h2>
              <p className="platform-subtitle">{current.subtitle}</p>
              <p className="platform-description">{current.description}</p>
            </div>
          </div>

          <div className="platform-modules">
            <h3>核心功能模块</h3>
            <div className="modules-grid">
              {current.modules.map((module, index) => (
                <div key={index} className="module-card">
                  <div className="module-index">0{index + 1}</div>
                  <p>{module}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="platform-gtm">
            <h3>落地价值与部署方式</h3>
            <div className="gtm-grid">
              {current.gtm.map((item, index) => (
                <div key={index} className="gtm-card">
                  <FiCheck />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="platform-cta">
            <div className="cta-header">
              <h3>{current.cta.title}</h3>
              <p>由合伙人、产品总监、技术专家组成的团队将为您提供专属解答与实施建议。</p>
            </div>
            <div className="cta-actions">
              {current.cta.actions.map((action, index) => (
                <a
                  key={index}
                  href={action.link}
                  className={`btn ${action.primary ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {action.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="platform-differentiators">
        <div className="container">
          <div className="diff-grid">
            <div className="diff-card">
              <div className="diff-icon"><FiBarChart /></div>
              <h4>数据中枢</h4>
              <p>构建造价治理数据底座，沉淀标准化指标、价格库、案例库，实现数据资产化管理。</p>
            </div>
            <div className="diff-card">
              <div className="diff-icon"><FiLock /></div>
              <h4>安全合规</h4>
              <p>支持等保、密评，满足政府、央国企严格的数据安全与合规要求。</p>
            </div>
            <div className="diff-card">
              <div className="diff-icon"><FiTrendingUp /></div>
              <h4>快速落地</h4>
              <p>成熟实施方法论 + 专业顾问陪伴，4-8 周即可完成试点上线。</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Platform;
